<script setup lang="ts">
import {SubLocation} from "@/api/dx-backend";
import {computed} from "vue";
import DefaultImage from "@/assets/images/backgrounds/AbstracPositionLoading.png"

interface Props {
  subLocation: SubLocation
  selected: boolean
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'copy-id': [subLocationId: string];
  'filter-by-location': [subLocation: SubLocation];
}>();

const imageUrl = computed(() => {
  return props.subLocation?.image || DefaultImage;
});

// Fast action handlers
const handleCopyId = (event: Event) => {
  event.stopPropagation();
  emit('copy-id', props.subLocation.id);
};

const handleFilterByLocation = (event: Event) => {
  event.stopPropagation();
  emit('filter-by-location', props.subLocation);
};
</script>

<template>
  <div class="selector-card" :class="{selected: props.selected}">
    <div class="image-container">
      <img :src="imageUrl" :alt="props.subLocation.name" class="location-image"/>

      <!-- Fast Actions Overlay -->
      <div class="fast-actions">
        <button
            class="action-btn"
            title="Copy SubLocation ID"
            @click="handleCopyId"
        >
          📋
        </button>
        <button
            class="action-btn"
            title="Filter by Location"
            @click="handleFilterByLocation"
            v-if="props.subLocation.location"
        >
          🗺️
        </button>
      </div>
    </div>
    <div class="sublocation-info">
      <h3 class="sublocation-name">{{ props.subLocation.name }}</h3>
      <p class="location-name">{{ props.subLocation.location || 'Unknown Location' }}</p>
      <p class="sublocation-description" v-if="props.subLocation.description">
        {{
          props.subLocation.description.length > 60 ? props.subLocation.description.substring(0, 60) + '...' : props.subLocation.description
        }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.selector-card {
  aspect-ratio: 4 / 5;
  display: flex;
  flex-direction: column;
  padding: var(--gm-gap-sm);
  border: 2px solid transparent;
  border-radius: var(--gm-radius-sm);
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  background: var(--gm-surface-1);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.selector-card:hover {
  background: var(--gm-surface-2);
}

.selector-card.selected {
  border-color: var(--gm-accent);
  background: var(--gm-accent-soft);
}

.image-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--gm-gap-xs);
  overflow: hidden;
  border-radius: var(--gm-radius-sm);
  position: relative;
  background: var(--gm-surface-sunken);
}

.location-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--gm-radius-sm);
}

.fast-actions {
  position: absolute;
  top: var(--gm-gap-xs);
  right: var(--gm-gap-xs);
  display: flex;
  flex-direction: column;
  gap: 0.175rem;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.selector-card:hover .fast-actions {
  opacity: 1;
}

.action-btn {
  width: 1.75rem;
  height: 1.75rem;
  border: 1px solid var(--gm-border);
  border-radius: 50%;
  background: var(--gm-surface-sunken);
  color: var(--gm-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.action-btn:hover {
  background: var(--gm-accent-soft);
  border-color: var(--gm-accent);
}

.sublocation-info {
  flex-shrink: 0;
  text-align: center;
}

.sublocation-name {
  margin: 0 0 0.175rem 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--gm-accent);
  font-family: var(--gm-font-display);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.location-name {
  margin: 0 0 0.175rem 0;
  font-size: 0.65rem;
  color: var(--gm-text-muted);
  font-weight: 500;
  line-height: 1.1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sublocation-description {
  margin: 0;
  font-size: 0.6rem;
  color: var(--gm-text-dim);
  line-height: 1.1;
}
</style>
